import React, { useState } from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { PhotoIcon } from '@heroicons/react/24/outline'

const themePresets = [
  {
    name: 'Black & Silver',
    primary: '#9CA3AF',
    background: '#000000',
    accent: '#9CA3AF',
    isDark: true
  },
  {
    name: 'Blue & Gray',
    primary: '#3B82F6',
    background: '#1F2937',
    accent: '#2563EB',
    isDark: true
  },
  {
    name: 'Green & Dark',
    primary: '#10B981',
    background: '#1a1a1a',
    accent: '#059669',
    isDark: true
  },
  {
    name: 'Purple & Dark',
    primary: '#8B5CF6',
    background: '#1a1a1a',
    accent: '#7C3AED',
    isDark: true
  },
  {
    name: 'Gold & Black',
    primary: '#D4AF37',
    background: '#1a1a1a',
    accent: '#B8952F',
    isDark: true
  },
  {
    name: 'Gold & White',
    primary: '#D4AF37',
    background: '#ffffff',
    accent: '#B8952F',
    isDark: false
  }
]

export default function BrandingStep() {
  const { wizardData, updateStepData } = useSetupWizard()
  const colors = useThemeColors()
  const { branding } = wizardData
  const [dragActive, setDragActive] = useState(false)

  const updateBranding = (field: string, value: string | boolean) => {
    updateStepData('branding', { [field]: value })
  }

  const handleThemeSelect = (preset: typeof themePresets[0]) => {
    updateStepData('branding', {
      primaryColor: preset.primary,
      backgroundColor: preset.background,
      accentColor: preset.accent,
      textColor: preset.isDark ? '#ffffff' : '#1a1a1a',
      darkMode: preset.isDark
    })
  }

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      updateBranding('logo', file)
      updateBranding('logoFile', file) // Store the actual file
      // Create preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        updateBranding('logoUrl', e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      updateBranding('logo', file)
      updateBranding('logoFile', file) // Store the actual file
      // Create preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        updateBranding('logoUrl', e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  return (
    <div className="branding-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Customize your appearance
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Choose your logo and colors. This will be used throughout your member portal and communications.
      </p>

      <div style={{
        display: 'grid',
        gap: '2rem'
      }}>
        {/* Logo Upload */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '0.5rem',
            color: colors.text
          }}>
            Logo (optional)
          </label>
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setDragActive(true)
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleFileDrop}
            style={{
              border: `2px dashed ${dragActive ? colors.primary : colors.border}`,
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              backgroundColor: dragActive ? `${colors.primary}10` : colors.background,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {branding.logoUrl ? (
              <div>
                <img
                  src={branding.logoUrl}
                  alt="Logo preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '100px',
                    margin: '0 auto 1rem'
                  }}
                />
                <p style={{
                  fontSize: '0.875rem',
                  color: colors.textSecondary
                }}>
                  Click or drag to replace
                </p>
              </div>
            ) : (
              <>
                <PhotoIcon style={{
                  width: '48px',
                  height: '48px',
                  color: colors.textMuted,
                  margin: '0 auto 1rem'
                }} />
                <p style={{
                  fontSize: '0.875rem',
                  color: colors.text,
                  marginBottom: '0.5rem'
                }}>
                  Drag and drop your logo here, or click to browse
                </p>
                <p style={{
                  fontSize: '0.813rem',
                  color: colors.textMuted
                }}>
                  Supports PNG, JPG, SVG (max 5MB)
                </p>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="logo-upload"
            />
            <label htmlFor="logo-upload" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              cursor: 'pointer'
            }} />
          </div>
        </div>

        {/* Theme Selection */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '1rem',
            color: colors.text
          }}>
            Choose a color theme
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {themePresets.map((preset, index) => {
              const isSelected = 
                branding.primaryColor === preset.primary &&
                branding.backgroundColor === preset.background
              
              return (
                <div
                  key={index}
                  onClick={() => handleThemeSelect(preset)}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    border: `2px solid ${isSelected ? colors.primary : colors.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: colors.background,
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = colors.primaryHover
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = colors.border
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: preset.primary,
                      border: '2px solid white'
                    }} />
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: preset.background,
                      border: '2px solid white'
                    }} />
                  </div>
                  <p style={{
                    fontSize: '0.813rem',
                    fontWeight: isSelected ? '600' : '400',
                    color: isSelected ? colors.text : colors.textSecondary,
                    margin: 0
                  }}>
                    {preset.name}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Custom Colors */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '1rem',
            color: colors.text
          }}>
            Or customize your colors
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.813rem',
                color: colors.textSecondary,
                marginBottom: '0.5rem'
              }}>
                Primary Color
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <input
                  type="color"
                  value={branding.primaryColor}
                  onChange={(e) => updateBranding('primaryColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={branding.primaryColor}
                  onChange={(e) => updateBranding('primaryColor', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.813rem',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.813rem',
                color: colors.textSecondary,
                marginBottom: '0.5rem'
              }}>
                Background Color
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <input
                  type="color"
                  value={branding.backgroundColor}
                  onChange={(e) => updateBranding('backgroundColor', e.target.value)}
                  style={{
                    width: '40px',
                    height: '40px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={branding.backgroundColor}
                  onChange={(e) => updateBranding('backgroundColor', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.813rem',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            marginBottom: '1rem',
            color: colors.text
          }}>
            Preview
          </label>
          <div style={{
            backgroundColor: branding.backgroundColor,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt="Logo"
                style={{
                  maxWidth: '150px',
                  maxHeight: '60px',
                  margin: '0 auto 1rem'
                }}
              />
            )}
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: branding.primaryColor,
              marginBottom: '0.5rem'
            }}>
              {wizardData.organizationProfile.businessName || 'Your Business Name'}
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: branding.darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
            }}>
              This is how your branding will appear to members
            </p>
            <button style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: branding.primaryColor,
              color: branding.backgroundColor,
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}>
              Sample Button
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}